import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { getTotalDebt } from './refinancing.helpers';
import { EMPTY_DEBT, TRefinancingDebt } from './refinancing.types';

const MAX_DEBTS = 6;

export const useRefinancingDebts = () => {
  const [targetDebtIndex, setTargetDebtIndex] = useState(0);
  const { control, watch } = useFormContext();

  const {
    fields: debts,
    append,
    remove,
  } = useFieldArray({ control, name: 'refinancing.debts' });

  const addDebt = () => {
    if (debts.length >= MAX_DEBTS) return;
    const newDebtIndex = debts.length;
    append({ ...EMPTY_DEBT });
    setTargetDebtIndex(newDebtIndex);
  };

  const removeDebt = (index: number) => {
    setTargetDebtIndex(Math.max(0, index - 1));
    remove(index);
  };

  const requestedAmount = convertCurrencyToNumber(watch('amount')) || 0;
  const watchedDebts: TRefinancingDebt[] = watch('refinancing.debts') ?? [];

  // Round each balance to whole pounds so the total matches the debt lines
  // shown below.
  const debtLines = watchedDebts
    .map((debt, index) => {
      const lenderName = debt?.lenderName?.trim();
      return {
        label: lenderName
          ? `Debt ${index + 1} - ${lenderName}`
          : `Debt ${index + 1}`,
        amount: Math.round(
          convertCurrencyToNumber(debt?.outstandingBalance) || 0,
        ),
      };
    })
    .filter((line) => line.amount > 0);

  const totalDebt = getTotalDebt(watchedDebts);
  const amountRemaining = requestedAmount - totalDebt;

  return {
    debts,
    targetDebtIndex,
    setTargetDebtIndex,
    addDebt,
    removeDebt,
    canAddDebt: debts.length < MAX_DEBTS,
    requestedAmount,
    totalDebt,
    amountRemaining,
    debtLines,
  };
};
